import { Link } from "react-router";
import { PosterRotator } from "../components/PosterRotator.js";
import { TrailerFacade } from "../components/TrailerFacade.js";
import { TRAILER } from "../content/trailer.js";
import { useT } from "../i18n/LocaleProvider.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";

/**
 * My Child — the first film under the banner. The trailer id lives in
 * content/trailer.ts; until it is set the page says the trailer is coming
 * rather than pretending.
 */

/** Credited exactly as the film's own lower-thirds set them. */
const PEOPLE = ["najib", "nora", "diba", "silk"] as const;

export function FilmPage() {
  const { m, lp } = useT();
  useDocumentTitle(m.meta.pages.film);

  return (
    <div className="container">
      <article className="film">
        <header className="film__head">
          <p className="section__kicker">{m.film.kicker}</p>
          <h1 className="film__title">{m.film.title}</h1>
          <p className="film__byline">{m.film.byline}</p>
          <p className="film__logline">{m.film.logline}</p>
          <dl className="film__facts">
            <div>
              <dt>{m.film.directorLabel}</dt>
              <dd>{m.film.director}</dd>
            </div>
            <div>
              <dt>{m.film.runtimeLabel}</dt>
              <dd>{m.film.runtime}</dd>
            </div>
            <div>
              <dt>{m.film.languageLabel}</dt>
              <dd>{m.film.language}</dd>
            </div>
          </dl>
        </header>

        <PosterRotator
          posters={[
            { slug: "portrait", alt: m.film.posters.portrait },
            { slug: "object", alt: m.film.posters.object },
            { slug: "dance", alt: m.film.posters.dance },
          ]}
        />

        <section className="film__section">
          <h2>{m.film.synopsisHeading}</h2>
          <p>{m.film.synopsis1}</p>
          <p>{m.film.synopsis2}</p>
        </section>

        {/* Before the trailer, not after it: a warning below the thing it warns about is decoration. */}
        <section className="film__section">
          <div className="card card--warning" role="note">
            <h2>{m.film.warningHeading}</h2>
            <p style={{ margin: 0 }}>{m.film.warning}</p>
          </div>
        </section>

        <section className="film__section" id="trailer">
          <p className="section__kicker">{m.film.trailerHeading}</p>
          <TrailerFacade youtubeId={TRAILER.youtubeId} thumbnailUrl={TRAILER.poster} title={m.film.title} />
          {!TRAILER.youtubeId && <p className="muted small">{m.film.trailerComing}</p>}
        </section>

        <section className="film__section">
          <h2>{m.film.titleHeading}</h2>
          <p>{m.film.titleOrigin}</p>
        </section>

        <section className="film__section">
          <h2>{m.film.peopleHeading}</h2>
          <p className="muted">{m.film.peopleNote}</p>
          <ul className="film__people">
            {PEOPLE.map((k) => (
              <li key={k} className="card">
                <h3>{m.people[k].name}</h3>
                <p className="muted small film__role">{m.people[k].role}</p>
                <p style={{ margin: 0 }}>{m.people[k].note}</p>
              </li>
            ))}
          </ul>
        </section>

        <figure className="film__still">
          <img src="/art/film/my-child-dance.jpg" alt="" width="1200" height="675" loading="lazy" />
        </figure>

        <section className="film__section">
          <h2>{m.film.screeningsHeading}</h2>
          <p>{m.film.screenings}</p>
          <Link className="btn btn--ghost" to={lp("/contact")}>
            {m.film.screeningsCta}
          </Link>
        </section>

        <section className="film__section film__back">
          <h2>{m.film.backHeading}</h2>
          <p>{m.film.backBody}</p>
          <Link className="btn btn--primary" to={lp("/share")}>
            {m.storyBlock.cta}
          </Link>
        </section>
      </article>
    </div>
  );
}
